import Event from '@mui/icons-material/Event';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import { type Dayjs, dayjs } from 'date';
import { forwardRef, useEffect, useState } from 'react';

import { type DateRange, DateRangePicker } from '../DateRangePicker/DateRangePicker';
import { Select, type SelectProps } from '../Select/Select';

export type SelectDatePickerProps = Omit<SelectProps, 'onChange' | 'value' | 'defaultValue'> & {
  onChange: ({ from, to }: DateRange) => void;
  options: { label: string; value: Dayjs }[];
  defaultValue: Dayjs | undefined;
  defaultRangeValue?: DateRange;
  anchorDate?: Dayjs;
  rangeSlotProps?: React.ComponentProps<typeof DateRangePicker>['slotProps'];
};

const DATE_RANGE_FORMAT = 'DD/MM/YYYY';
const DEFAULT_FROM_LABEL = 'From';
const DEFAULT_TO_LABEL = 'To';
const DEFAULT_RANGE_DURATION = { unit: 'week' as const, value: 1 };

const formatDateValue = (date: Dayjs | undefined) => (date ? date.toISOString() : undefined);
const formatDateRangeValue = (rangeValue: DateRange) =>
  `${rangeValue.from.format(DATE_RANGE_FORMAT)} - ${rangeValue.to.format(DATE_RANGE_FORMAT)}`;
const buildRangeFromValue = (value: Dayjs | undefined, anchorDate: Dayjs): DateRange => {
  if (!value) {
    return {
      from: anchorDate,
      to: anchorDate.add(DEFAULT_RANGE_DURATION.value, DEFAULT_RANGE_DURATION.unit),
    };
  }

  const selectedDate = dayjs(value);

  return selectedDate.isBefore(anchorDate)
    ? {
        from: selectedDate,
        to: anchorDate,
      }
    : {
        from: anchorDate,
        to: selectedDate,
      };
};

export const SelectDatePicker = forwardRef(function SelectDatePicker(
  { onChange, options, defaultValue, defaultRangeValue, anchorDate, rangeSlotProps, ...rest }: SelectDatePickerProps,
  ref
) {
  const resolvedAnchorDate = anchorDate ?? dayjs();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<Dayjs | undefined | 'range'>(defaultRangeValue ? 'range' : defaultValue);
  const [rangeValue, setRangeValue] = useState<DateRange>(
    defaultRangeValue ?? buildRangeFromValue(defaultValue, resolvedAnchorDate)
  );

  useEffect(() => {
    const nextAnchorDate = anchorDate ?? dayjs();

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue(defaultRangeValue ? 'range' : defaultValue);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRangeValue(defaultRangeValue ?? buildRangeFromValue(defaultValue, nextAnchorDate));
  }, [anchorDate, defaultRangeValue, defaultValue]);

  const selectValue = value === 'range' ? 'range' : formatDateValue(value);
  const pickerDefaultRangeValue =
    value === 'range' ? rangeValue : buildRangeFromValue(defaultValue, resolvedAnchorDate);

  return (
    <Select
      startAdornment={
        <InputAdornment position="start">
          <Event />
        </InputAdornment>
      }
      data-testid="select-date-picker"
      value={value ? selectValue : ''}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;

        const isClickOnSelect = target.getAttribute('role') === 'combobox';
        if (isClickOnSelect) {
          setOpen(true);
          return;
        }

        const isClickOutsideComponent = target.classList.contains('MuiBackdrop-root');
        if (isClickOutsideComponent) {
          setOpen(false);
        }
      }}
      onChange={(event) => {
        event.stopPropagation();
        if (!event.target.value || event.target.value === 'range') {
          return;
        }
        setValue(dayjs(event.target.value));

        const selectedDate = dayjs(event.target.value);
        const currentAnchor = anchorDate ?? dayjs();

        if (selectedDate.isBefore(currentAnchor)) {
          onChange({
            from: selectedDate,
            to: currentAnchor,
          });
          setOpen(false);
          return;
        }
        onChange({
          from: currentAnchor,
          to: selectedDate,
        });
        setOpen(false);
      }}
      renderValue={(value) => {
        if (value === 'range') {
          return formatDateRangeValue(rangeValue);
        }
        const selectedOption = options.find((option) => option.value.isSame(value, 'date'));
        return selectedOption ? selectedOption.label : value.toString();
      }}
      defaultValue={formatDateValue(defaultValue)}
      {...rest}
      open={open}
      ref={ref}
    >
      <MenuItem value="range">
        <DateRangePicker
          onChange={({ from, to }) => {
            setValue('range');

            onChange({
              from,
              to,
            });

            setRangeValue({ from, to });
          }}
          onAccept={() => {
            setOpen(false);
          }}
          defaultValues={pickerDefaultRangeValue}
          slotProps={{
            from: {
              label: rangeSlotProps?.from?.label ?? DEFAULT_FROM_LABEL,
              size: rangeSlotProps?.from?.size,
            },
            separator: rangeSlotProps?.separator,
            to: {
              label: rangeSlotProps?.to?.label ?? DEFAULT_TO_LABEL,
              size: rangeSlotProps?.to?.size,
            },
          }}
        />
      </MenuItem>

      {options.map(({ label, value }) => (
        <MenuItem key={label} value={value.toISOString()}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );
});
